import isEqual from 'lodash/isEqual';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

import { EXCLUDED_MODALS_FROM_PERSISTENT } from './constants';
import type { ModalItem } from './types';

type State = {
  /**
   * Determines opened modals
   *
   * @default []
   */
  modals: ModalItem[];
};

type Actions = {
  hideModal: (modalId?: string) => void;
  showModal: (modalId: string, params?: Record<string, unknown>) => void;
};

type ModalState = State & Actions;

const defaultState: State = {
  modals: [],
};

export const useModalState = create<ModalState>()(
  persist(
    (set) => ({
      ...defaultState,

      hideModal: (modalId) => {
        return set((prev) => {
          if (modalId) {
            return { ...prev, modals: prev.modals.filter((modal) => modal.id !== modalId) };
          }

          return { ...prev, modals: prev.modals.slice(0, -1) };
        });
      },

      showModal: (id, params) => {
        return set((prev) => {
          const exists = prev.modals.some((m) => isEqual(m.id, id));

          if (exists) {
            return {
              ...prev,
              modals: prev.modals.map((m) => (isEqual(m.id, id) ? { ...m, params: params ?? m.params } : m)),
            };
          }

          return { ...prev, modals: [...prev.modals, { id, params }] };
        });
      },
    }),
    {
      name: 'app.modal',
      version: 2,
      storage: createJSONStorage(() => sessionStorage),
      partialize: (state) => ({
        modals: state.modals.filter(({ id }) => !EXCLUDED_MODALS_FROM_PERSISTENT.includes(id)),
      }),
      merge: (persisted, current) => ({ ...current, ...(persisted as ModalState) }),
      migrate: (state, version) => {
        if (version < 2) {
          return { ...defaultState };
        }

        return state;
      },
    },
  ),
);
